import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, List, ChevronDown, ChevronUp, Clock, Check, Settings } from 'lucide-react';
import { Workout } from '@/types/workout';
import { WorkoutExerciseSetDTO } from '@/types/workout';
import { ExerciseView } from './ExerciseView';
import { RestTimer } from './RestTimer';
import { SessionComplete } from './SessionComplete';
import { useSessionStore } from '@/lib/stores/sessionStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { workoutApi } from '@/services/workoutApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

interface WorkoutSessionProps {
  workout: Workout;
  onExit: () => void;
}

type SessionView = 'exercise' | 'rest' | 'complete';

export function WorkoutSession({ workout: initialWorkout, onExit }: WorkoutSessionProps) {
  const [view, setView] = useState<SessionView>('exercise');
  const [isPaused, setIsPaused] = useState(false);
  const [currentSet, setCurrentSet] = useState<WorkoutExerciseSetDTO | null>(null);
  const [restSeconds, setRestSeconds] = useState<number | null>(null);
  const [showOverview, setShowOverview] = useState(false);
  const [expandedExerciseIndex, setExpandedExerciseIndex] = useState<number | null>(null);
  const [workout, setWorkout] = useState<Workout>(initialWorkout);
  const [overviewSetEdits, setOverviewSetEdits] = useState<{[setId: string]: {reps?: number, weight?: number, duration_seconds?: number, rpe?: number}}>({});
  const [isResting, setIsResting] = useState(false);
  const [restTimeRemaining, setRestTimeRemaining] = useState(0);
  const [autoStartSet, setAutoStartSet] = useState(false);
  const [showEndWorkoutModal, setShowEndWorkoutModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [completedExerciseIds, setCompletedExerciseIds] = useState<Set<string>>(new Set());
  
  // Timer state - calculated locally from setStartTime
  const [setTimeRemaining, setSetTimeRemaining] = useState(0);
  const [isTimeBasedSet, setIsTimeBasedSet] = useState(false);
  const [isSetInProgress, setIsSetInProgress] = useState(false);
  const [setStartTime, setSetStartTime] = useState<Date | null>(null);
  
  // Track last completed set to prevent duplicate completions
  const lastCompletedSetIdRef = useRef<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const soundPlayedRef = useRef(false);
  
  // Track completed sets for back navigation
  const [completedSetsHistory, setCompletedSetsHistory] = useState<WorkoutExerciseSetDTO[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1); // -1 means at current set, 0+ means navigating back
  
  const { 
    startSession, 
    pauseSession, 
    resumeSession, 
    completeSet, 
    completeSession, 
    syncProgress, 
    sessionProgress, 
    clearSession, 
    updateSetMetadata, 
    sessionId 
  } = useSessionStore();
  
  // Initialize audio context for notifications
  useEffect(() => {
    if (typeof window !== 'undefined' && 'AudioContext' in window) {
      audioContextRef.current = new AudioContext();
    }
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!audioContextRef.current || soundPlayedRef.current) return;
    
    try {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.5);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 0.5);
      
      soundPlayedRef.current = true;
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }, []);
  
  // Initialize session on mount
  useEffect(() => {
    startSession(workout.id).catch((error) => {
      console.error('Failed to start session:', error);
    });

    // Fetch full workout data for overview
    const fetchWorkout = async () => {
      try {
        const fullWorkout = await workoutApi.getWorkout(workout.id);
        setWorkout(fullWorkout);
      } catch (error) {
        console.error('Failed to fetch workout:', error);
      }
    };
    fetchWorkout();

    return () => {
      clearSession();
    };
  }, [workout.id]);

  // Update UI state from session progress (single source of truth)
  useEffect(() => {
    if (!sessionProgress) return;

    // Only update from backend if we're not navigating through history
    if (historyIndex === -1) {
      // Update current set
      setCurrentSet(sessionProgress.current_set || null);
      
      // Check if session is completed
      if (sessionProgress.session_status === 'completed' || !sessionProgress.current_set) {
        setView('complete');
        return;
      }
    }
    
    // Update pause state
    setIsPaused(sessionProgress.session_status === 'paused');
    
    // Check if this is a time-based set
    const set = historyIndex >= 0 && completedSetsHistory[historyIndex] 
      ? completedSetsHistory[historyIndex]
      : sessionProgress.current_set;
    
    if (set?.duration_seconds) {
      setIsTimeBasedSet(true);
    } else {
      setIsTimeBasedSet(false);
      setSetTimeRemaining(0);
    }
    
    const currentSetId = set?.set_id;
    
    // Restore setStartTime from backend if set is in progress (on mount or page refresh)
    // Only restore if we don't already have a local start time and we're at current set
    if (historyIndex === -1 && currentSetId && set?.duration_seconds && sessionProgress.set_start_time && !setStartTime) {
      // Restore start time from backend
      const backendStartTime = new Date(sessionProgress.set_start_time);
      setSetStartTime(backendStartTime);
      setIsSetInProgress(true);
    }
    
    // Reset completion tracking when set changes
    if (currentSetId && lastCompletedSetIdRef.current && lastCompletedSetIdRef.current !== currentSetId) {
      lastCompletedSetIdRef.current = null;
      soundPlayedRef.current = false;
      // Reset timer state when set changes (only if navigating forward, not back)
      if (historyIndex === -1) {
        setSetStartTime(null);
        setIsSetInProgress(false);
        if (set?.duration_seconds) {
          setSetTimeRemaining(set.duration_seconds);
        }
      }
    }
  }, [sessionProgress?.current_set?.set_id, sessionProgress?.current_set?.reps, sessionProgress?.current_set?.weight, sessionProgress?.current_set?.rpe, sessionProgress?.current_set?.duration_seconds, sessionProgress?.session_status, sessionProgress?.set_start_time, historyIndex, completedSetsHistory]);

  // Calculate timer purely locally from setStartTime
  useEffect(() => {
    if (!isTimeBasedSet || !currentSet?.duration_seconds || !setStartTime || isPaused) {
      return;
    }

    const updateTimer = () => {
      if (!setStartTime || !currentSet?.duration_seconds) return;
      
      const elapsed = Math.floor((Date.now() - setStartTime.getTime()) / 1000);
      const remaining = Math.max(0, currentSet.duration_seconds - elapsed);
      
      setSetTimeRemaining(remaining);
      
      // Auto-complete if timer expired
      if (remaining === 0 && !soundPlayedRef.current && audioContextRef.current) {
        playNotificationSound();
        
        // Auto-complete set when timer expires
        if (lastCompletedSetIdRef.current !== currentSet.set_id) {
          lastCompletedSetIdRef.current = currentSet.set_id;
          completeSet(currentSet.set_id).catch((error) => {
            console.error('Failed to auto-complete set:', error);
            lastCompletedSetIdRef.current = null;
          });
        }
      }
    };

    // Update immediately
    updateTimer();

    // Update every second for display
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [isTimeBasedSet, currentSet?.set_id, currentSet?.duration_seconds, setStartTime, isPaused, playNotificationSound, completeSet]);


  // Rest timer - simple countdown from initial value
  useEffect(() => {
    if (currentSet && currentSet.rest_seconds > 0 && view === 'rest' && !isResting) {
      setIsResting(true);
      setRestTimeRemaining(currentSet.rest_seconds);
    }
  }, [currentSet?.rest_seconds, view]);

  useEffect(() => {
    if (isResting && restTimeRemaining > 0 && !isPaused) {
      const timer = setTimeout(() => {
        setRestTimeRemaining((prev) => {
          if (prev <= 1) {
            setIsResting(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isResting, restTimeRemaining, isPaused]);

  // Calculate exercise progress
  const totalExercises = workout.exercises.length;
  const exerciseProgress = totalExercises > 0 
    ? (completedExerciseIds.size / totalExercises) * 100 
    : 0;
  
  // Track completed exercises based on completed sets history
  useEffect(() => {
    if (completedSetsHistory.length > 0) {
      const exerciseSetCounts = new Map<string, number>();
      
      // Count completed sets per exercise
      completedSetsHistory.forEach(set => {
        const exerciseId = set.exercise.exercise_id;
        exerciseSetCounts.set(exerciseId, (exerciseSetCounts.get(exerciseId) || 0) + 1);
      });
      
      // Check which exercises are fully completed
      const newlyCompleted = new Set<string>();
      workout.exercises.forEach(exercise => {
        const totalSetsForExercise = exercise.target.mode === 'reps' && exercise.target.sets
          ? exercise.target.sets
          : 1;
        const completedSets = exerciseSetCounts.get(exercise.exercise_id) || 0;
        
        if (completedSets >= totalSetsForExercise) {
          newlyCompleted.add(exercise.exercise_id);
        }
      });
      
      setCompletedExerciseIds(newlyCompleted);
    }
  }, [completedSetsHistory, workout.exercises]);

  const handleExerciseComplete = async (data?: {
    actual_reps?: number;
    actual_weight?: number;
    actual_duration_seconds?: number;
    actual_rpe?: number;
  }) => {
    if (!currentSet) return;

    try {
      const setId = currentSet.set_id;
      lastCompletedSetIdRef.current = setId;
      soundPlayedRef.current = false;
      
      // If we're navigating back, don't complete the set - just go forward in history
      if (historyIndex >= 0) {
        // Moving forward from history - go to next item or current set
        if (historyIndex < completedSetsHistory.length - 1) {
          // Go forward in history
          setHistoryIndex(historyIndex + 1);
          setCurrentSet(completedSetsHistory[historyIndex + 1]);
          setView('exercise');
        } else {
          // Reached the end of history, go back to current set
          setHistoryIndex(-1);
          await syncProgress(); // Sync to get current set from backend
          setView('exercise');
        }
        return;
      }
      
      // Add current set to history before completing (only if not already in history)
      const isAlreadyInHistory = completedSetsHistory.some(s => s.set_id === currentSet.set_id);
      if (!isAlreadyInHistory) {
        setCompletedSetsHistory(prev => [...prev, { ...currentSet }]);
      }
      
      await completeSet(setId, data);

      // Sync progress to get updated state (completeSet already syncs, but ensure we have latest)
      await syncProgress();

      // Get the fresh sessionProgress from the store after syncing
      const freshProgress = useSessionStore.getState().sessionProgress;

      // Check if there's a next set after completing
      if (!freshProgress?.current_set || freshProgress.session_status === 'completed') {
        // No more sets, session is complete - mark all exercises as complete
        const allExerciseIds = new Set(workout.exercises.map(e => e.exercise_id));
        setCompletedExerciseIds(allExerciseIds);
        setView('complete');
        return;
      }

      // Check if there's a rest period for the completed set
      if (currentSet.rest_seconds > 0) {
        setRestSeconds(currentSet.rest_seconds);
        setIsResting(true);
        setRestTimeRemaining(currentSet.rest_seconds);
        setView('rest');
      } else {
        setView('exercise');
      }
    } catch (error) {
      console.error('Failed to complete set:', error);
      lastCompletedSetIdRef.current = null;
    }
  };

  const handleRestComplete = async () => {
    lastCompletedSetIdRef.current = null;
    soundPlayedRef.current = false;
    setIsResting(false);
    setRestTimeRemaining(0);
    setRestSeconds(null);
    
    // Sync progress to get next set
    await syncProgress();
    
    // Get the fresh sessionProgress from the store after syncing
    const freshProgress = useSessionStore.getState().sessionProgress;
    
    // Check if session is complete
    if (!freshProgress?.current_set || freshProgress.session_status === 'completed') {
      setView('complete');
      return;
    }
    
    setView('exercise');
  };

  const handleSkipExercise = async () => {
    if (!currentSet || !sessionId || historyIndex >= 0) return;
    
    // If we're in rest view, exit rest first
    if (view === 'rest') {
      setIsResting(false);
      setRestTimeRemaining(0);
      setRestSeconds(null);
      setView('exercise');
      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    try {
      // Complete the current set without data (skipping it)
      const setId = currentSet.set_id;
      lastCompletedSetIdRef.current = setId;
      soundPlayedRef.current = false;
      
      // Complete the set (this will skip rest periods automatically since we're not entering rest view)
      await completeSet(setId);
      
      // Sync progress to get next set
      await syncProgress();
      
      // Get the fresh sessionProgress from the store after syncing
      const freshProgress = useSessionStore.getState().sessionProgress;
      
      // Check if session is complete
      if (!freshProgress?.current_set || freshProgress.session_status === 'completed') {
        setView('complete');
        return;
      }
      
      // Update current set and view
      setCurrentSet(freshProgress.current_set);
      setView('exercise');
      
      // Reset state
      lastCompletedSetIdRef.current = null;
      soundPlayedRef.current = false;
      setIsResting(false);
      setRestTimeRemaining(0);
      setRestSeconds(null);
      
    } catch (error) {
      console.error('Failed to skip set:', error);
      // Sync progress to recover state
      await syncProgress();
    }
  };
  
  const handleSaveOverviewSetEdit = useCallback(async (setId: string) => {
    const edits = overviewSetEdits[setId];
    if (!edits || !sessionId) return;

    try {
      await updateSetMetadata(setId, edits);
      setOverviewSetEdits(prev => {
        const newEdits = { ...prev };
        delete newEdits[setId];
        return newEdits;
      });
      // updateSetMetadata already calls syncProgress internally
    } catch (error) {
      console.error('Failed to update set metadata:', error);
    }
  }, [sessionId, overviewSetEdits, updateSetMetadata]);
  
  const handleStartSet = useCallback(async () => {
    // Don't start set if we're navigating through history
    if (historyIndex >= 0) return;
    
    if (currentSet?.set_id && !isSetInProgress && sessionId) {
      // Store local start time immediately
      const startTime = new Date();
      setSetStartTime(startTime);
      setIsSetInProgress(true);
      
      // Call API to start set on backend
      const { workoutSessionApi } = await import('@/services/workoutSessionApi');
      await workoutSessionApi.startSet(sessionId, currentSet.set_id);
      
      // Sync once to get updated progress (for next set info, but don't rely on it for timer)
      await syncProgress();
      
      if (currentSet.duration_seconds) {
        setIsTimeBasedSet(true);
        soundPlayedRef.current = false; // Reset sound flag for new set
      }
    }
  }, [currentSet?.set_id, currentSet?.duration_seconds, isSetInProgress, sessionId, syncProgress, historyIndex]);
  
  const handleGoToPrevious = useCallback(() => {
    if (completedSetsHistory.length === 0) return;
    
    // If we're at current set, go to last completed set
    if (historyIndex === -1) {
      const lastCompleted = completedSetsHistory[completedSetsHistory.length - 1];
      setHistoryIndex(completedSetsHistory.length - 1);
      setCurrentSet(lastCompleted);
      setView('exercise');
    } else if (historyIndex > 0) {
      // Go further back
      setHistoryIndex(historyIndex - 1);
      setCurrentSet(completedSetsHistory[historyIndex - 1]);
      setView('exercise');
    }
  }, [completedSetsHistory, historyIndex]);
  
  const handleGoToNext = useCallback(async () => {
    if (historyIndex < 0) return; // Already at current set
    
    if (historyIndex < completedSetsHistory.length - 1) {
      // Go forward in history
      setHistoryIndex(historyIndex + 1);
      setCurrentSet(completedSetsHistory[historyIndex + 1]);
      setView('exercise');
    } else {
      // Reached the end of history, go back to current set
      setHistoryIndex(-1);
      await syncProgress(); // Sync to get current set from backend
      setView('exercise');
    }
  }, [completedSetsHistory, historyIndex, syncProgress]);

  // Auto-start set when transitioning from rest to exercise with toggle enabled
  useEffect(() => {
    if (view === 'exercise' && autoStartSet && !isPaused && currentSet && currentSet.duration_seconds && !isSetInProgress && sessionId) {
      // Small delay to ensure view has fully transitioned and state is updated
      const timer = setTimeout(() => {
        handleStartSet();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [view, autoStartSet, isPaused, currentSet?.set_id, currentSet?.duration_seconds, isSetInProgress, sessionId, handleStartSet]);
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTogglePause = async () => {
    try {
      if (isPaused) {
        await resumeSession();
        setIsPaused(false);
      } else {
        await pauseSession();
        setIsPaused(true);
      }
    } catch (error) {
      console.error('Failed to toggle pause:', error);
      // Revert UI state on error
      setIsPaused(!isPaused);
    }
  };

  const handleEndWorkout = async () => {
    try {
      await completeSession();
      setShowEndWorkoutModal(false);
      onExit();
    } catch (error) {
      console.error('Failed to end workout:', error);
    }
  };

  // Render workout overview
  const renderWorkoutOverview = () => {
    const currentSetId = sessionProgress?.current_set?.set_id;
    
    return (
      <div className="w-full space-y-4 p-4 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Workout Overview</h3>
          <Button
            onClick={() => setShowOverview(false)}
            variant="outline"
            size="sm"
          >
            Back to Current Set
          </Button>
        </div>

        {/* Progress indicator */}
        {sessionProgress && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Progress:</span>
              <span>{completedExerciseIds.size} / {totalExercises} exercises completed</span>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${exerciseProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Rest Timer */}
        {isResting && (
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-600" />
                <span className="font-medium">Rest Timer:</span>
              </div>
              <div className="text-2xl font-bold text-orange-600">
                {formatTime(restTimeRemaining)}
              </div>
            </div>
            <Button
              onClick={handleRestComplete}
              variant="ghost"
              size="sm"
              className="w-full mt-2"
            >
              Skip Rest
            </Button>
          </div>
        )}

        {/* Exercises List */}
        <div className="space-y-1">
          {workout.exercises.map((exercise, idx) => {
            const isExpanded = expandedExerciseIndex === idx;
            const firstSet = exercise.target.mode === 'reps' && exercise.target.sets 
              ? { reps: exercise.target.reps, weight: exercise.target.weight, rest_seconds: exercise.rest_seconds }
              : { duration_seconds: exercise.target.duration_seconds, rest_seconds: exercise.rest_seconds };
            
            const isCurrentExercise = currentSet?.exercise?.exercise_id === exercise.exercise_id;

            return (
              <div key={idx} className={`border-b border-gray-200 last:border-0 ${isCurrentExercise ? 'bg-blue-50' : ''}`}>
                {/* Compact Row */}
                <div
                  onClick={() => setExpandedExerciseIndex(expandedExerciseIndex === idx ? null : idx)}
                  className={`flex items-center justify-between py-3 px-2 cursor-pointer hover:bg-gray-50 transition-colors ${
                    exercise.superset_group_id ? 'border-l-4 border-orange-500 pl-3' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {exercise.superset_group_id && (
                        <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
                          Superset {exercise.superset_group_id.replace('superset-', '')}
                        </span>
                      )}
                      {isCurrentExercise && (
                        <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                          Current
                        </span>
                      )}
                      <span className="text-sm font-medium truncate">
                        {exercise.name}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                    {exercise.target.mode === 'reps' && exercise.target.sets && (
                      <span>
                        {exercise.target.sets} sets × {exercise.target.reps} reps
                        {exercise.target.weight && ` @ ${exercise.target.weight}kg`}
                      </span>
                    )}
                    {exercise.target.mode === 'time' && exercise.target.duration_seconds && (
                      <span>
                        {exercise.target.duration_seconds}s
                      </span>
                    )}
                    {exercise.rest_seconds > 0 && (
                      <span>Rest: {exercise.rest_seconds}s</span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-4 py-3 bg-gray-50 space-y-3 text-sm">
                    {exercise.instruction && (
                      <p className="text-gray-600 leading-relaxed">
                        {exercise.instruction}
                      </p>
                    )}
                    
                    {/* Set breakdown - simplified for now since we don't have individual set IDs in ExerciseBlock */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Sets:
                      </h4>
                      <div className="space-y-2">
                        {exercise.target.mode === 'reps' && exercise.target.sets && (
                          Array.from({ length: exercise.target.sets }).map((_, setIdx) => (
                            <div key={setIdx} className="p-2 rounded bg-white">
                              <span className="text-gray-500 font-medium text-xs">
                                Set {setIdx + 1}:
                              </span>
                              <div className="mt-1">
                                {exercise.target.reps} reps
                                {exercise.target.weight && ` @ ${exercise.target.weight}kg`}
                                {exercise.rest_seconds > 0 && (
                                  <span className="text-gray-500">, rest {exercise.rest_seconds}s</span>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                        {exercise.target.mode === 'time' && exercise.target.duration_seconds && (
                          <div className="p-2 rounded bg-white">
                            <span className="text-gray-500 font-medium text-xs">Duration:</span>
                            <div className="mt-1">
                              {exercise.target.duration_seconds}s
                              {exercise.rest_seconds > 0 && (
                                <span className="text-gray-500">, rest {exercise.rest_seconds}s</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (view === 'complete') {
    return <SessionComplete onHome={onExit} />;
  }

  // Show overview if requested
  if (showOverview) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="sticky top-0 bg-white border-b border-neutral-200 z-10">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-xl font-semibold text-neutral-900">{workout.name}</h1>
              <div className="flex items-center gap-4">
              <button
                onClick={() => setShowSettingsModal(true)}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5 text-neutral-600" />
              </button>
              <button
                onClick={() => setShowEndWorkoutModal(true)}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-neutral-600" />
              </button>
              </div>
            </div>
          </div>
        </div>
        {renderWorkoutOverview()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-neutral-200 z-10">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-xl font-semibold text-neutral-900">{workout.name}</h1>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowSettingsModal(true)}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5 text-neutral-600" />
              </button>
              <button
                onClick={() => setShowEndWorkoutModal(true)}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-neutral-600" />
              </button>
            </div>
          </div>
          <div className="w-full bg-neutral-200 rounded-full h-1.5">
            <motion.div
              className="bg-neutral-900 h-1.5 rounded-full"
              initial={{ width: `${exerciseProgress}%` }}
              animate={{ width: `${exerciseProgress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="text-sm text-neutral-600">
              {completedExerciseIds.size} of {totalExercises} exercises
            </div>
          </div>
        </div>
      </div>

      {/* Overview Button at Bottom */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-20">
        <Button
          onClick={() => setShowOverview(true)}
          variant="ghost"
          size="sm"
          className="text-neutral-600 hover:text-neutral-900"
        >
          <List className="h-4 w-4 mr-2" />
          Overview
        </Button>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {view === 'exercise' && currentSet && (
          <motion.div
            key={`set-${currentSet.set_id}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ExerciseView
              currentSet={currentSet}
              onComplete={handleExerciseComplete}
              onSkip={handleSkipExercise}
              onSaveMetadata={updateSetMetadata}
              setTimeRemaining={isTimeBasedSet ? setTimeRemaining : undefined}
              onStartSet={handleStartSet}
              isSetInProgress={isSetInProgress}
              onPrevious={historyIndex >= 0 || completedSetsHistory.length > 0 ? handleGoToPrevious : undefined}
              onNext={historyIndex >= 0 ? handleGoToNext : undefined}
              isNavigatingHistory={historyIndex >= 0}
            />
          </motion.div>
        )}

        {view === 'rest' && restSeconds !== null && (
          <motion.div
            key="rest"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <RestTimer
              duration={restSeconds}
              onComplete={handleRestComplete}
              onSkip={handleRestComplete}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* End Workout Modal */}
      <Dialog open={showEndWorkoutModal} onOpenChange={setShowEndWorkoutModal}>
        <DialogContent>
          <DialogHeader className="mb-6">
            <DialogTitle>End workout early?</DialogTitle>
          </DialogHeader>
          <DialogFooter className="flex-col gap-3 mt-6">
            <Button
              variant="default"
              onClick={handleEndWorkout}
              className="w-full bg-neutral-900 text-white hover:bg-neutral-800"
            >
              End Workout
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowEndWorkoutModal(false)}
              className="w-full"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent>
          <DialogHeader className="mb-6">
            <DialogTitle className="text-left">Workout Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mb-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-1">
                <label htmlFor="auto-start-toggle-settings" className="block text-sm font-medium text-neutral-900">
                  Auto Start Set
                </label>
                <p className="text-sm text-neutral-600">
                  Automatically start rest timer after completing a set.
                </p>
              </div>
              <div className="flex-shrink-0 pt-1">
                <Switch
                  id="auto-start-toggle-settings"
                  checked={autoStartSet}
                  onCheckedChange={setAutoStartSet}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="default"
              onClick={() => setShowSettingsModal(false)}
              className="w-full bg-neutral-900 text-white hover:bg-neutral-800"
              size="lg"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

